<template>
  <small-modal-container :is-visible="userStore.nicknamePopup" @close="cancelNicknamePopup">
    <div class="nickname-content">
      <div class="nickname-title">닉네임을 입력해주세요</div>
      <input ref="nicknameInputRef" v-model="nicknameInput" :disabled="nicknameLoading" maxlength="10"
        placeholder="닉네임 (2~10자)" @input="validateNickname" class="nickname-input" />
      <div v-if="nicknameError" class="nickname-error">
        {{ nicknameError }}
      </div>
      <br />

      <div class="nickname-actions">
        <button v-if="!isFirstNickname" @click="cancelNicknamePopup" :disabled="nicknameLoading" class="cancel-button">
          취소
        </button>
        <button v-if="isFirstNickname" @click="logOut" :disabled="nicknameLoading" class="logout-button">
          로그아웃
        </button>
        <button @click="saveNickname" :disabled="isNicknameSaveDisabled" class="save-button">
          저장
        </button>
      </div>
    </div>
  </small-modal-container>
</template>

<script>
import { useUserStore } from "@/store/user";
import { setNickname as setNicknameApi } from "@/utils/write/user";
import SmallModalContainer from "@/components/modal/SmallModalContainer.vue";

export default {
  name: "NicknamePopup",
  components: {
    SmallModalContainer,
  },
  computed: {
    userStore() {
      return useUserStore();
    },
    isNicknameSaveDisabled() {
      return this.nicknameLoading || !this.nicknameInput.trim() || !!this.nicknameError;
    },
    isFirstNickname() {
      // 닉네임이 없으면 true (최초 등록)
      return !this.userStore.nickname;
    },
  },
  data() {
    return {
      nicknameInput: "",
      nicknameLoading: false,
      nicknameError: "",
    };
  },
  watch: {
    // 닉네임이 없으면 팝업 표시, 있으면 닫기
    "userStore.nickname": {
      immediate: true,
      handler(val) {
        if (this.userStore.isLogin && !val) {
          // 로그인했지만 닉네임이 없을 때만 팝업 표시
          this.userStore.nicknamePopup = true;
        } else {
          // 닉네임이 있거나 로그인하지 않았으면 팝업 숨김
          this.userStore.nicknamePopup = false;
        }
      },
    },
    // 팝업이 열릴 때 현재 닉네임을 input에 설정
    "userStore.nicknamePopup": {
      handler(isOpen) {
        if (isOpen) {
          // 닉네임이 있을 때만 input에 설정 (닉네임 변경 모드)
          this.nicknameInput = this.userStore.nickname;
          this.nicknameError = "";

          // DOM 렌더링 후 input에 포커스
          this.$nextTick(() => {
            if (this.$refs.nicknameInputRef) {
              this.$refs.nicknameInputRef.focus();
              // 텍스트가 있으면 전체 선택
              if (this.nicknameInput) {
                this.$refs.nicknameInputRef.select();
              }
            }
          });
        }
      },
    },
    // 라우트 변경 시 닉네임 팝업 닫기
    $route() {
      if (this.userStore.nicknamePopup) {
        this.userStore.nicknamePopup = false;
      }
    },
  },
  methods: {
    cancelNicknamePopup() {
      this.userStore.nicknamePopup = false;
    },
    logOut() {
      this.userStore.nicknamePopup = false; // 닉네임 팝업 닫기
      this.userStore.logOut();
      // 카카오 로그아웃도 함께 처리
      if (window.Kakao && window.Kakao.Auth) {
        window.Kakao.Auth.logout();
      }
      // 메인(오프) 화면으로 이동
      this.$router.push("/");
    },
    async validateNickname() {
      const nickname = this.nicknameInput.trim();
      if (!nickname) {
        this.nicknameError = "닉네임을 입력해주세요.";
        return false;
      }
      if (nickname.length < 2) {
        this.nicknameError = "닉네임은 최소 2자 이상이어야 합니다.";
        return false;
      }
      if (nickname.length > 10) {
        this.nicknameError = "닉네임은 최대 10자까지 가능합니다.";
        return false;
      }
      if (/\s/.test(nickname)) {
        this.nicknameError = "닉네임에는 공백을 포함할 수 없습니다.";
        return false;
      }
      // 중복 체크
      const { checkNicknameDuplicate } = await import("@/utils/read/user");
      const isAvailable = await checkNicknameDuplicate(nickname, this.userStore.email);
      if (!isAvailable) {
        this.nicknameError = "이미 사용 중인 닉네임입니다.";
        return false;
      }
      this.nicknameError = "";
      return true;
    },
    async saveNickname() {
      this.nicknameLoading = true;
      try {
        // Firebase Auth 연결 상태 확인
        const { checkAuthConnection } = await import("@/utils/auth");
        const isConnected = await checkAuthConnection();

        if (!isConnected) {
          this.nicknameError = "인증 연결이 만료되었습니다. 다시 로그인해주세요.";
          this.nicknameLoading = false;
          return;
        }

        const valid = await this.validateNickname();
        if (!valid) {
          this.nicknameLoading = false;
          return;
        }

        const nickname = this.nicknameInput.trim();

        await setNicknameApi(this.userStore.email, nickname);

        // 스토어에 닉네임 설정
        this.userStore.setNickname(nickname);

        // DOM 업데이트 후 팝업 닫기
        await this.$nextTick();
        this.userStore.nicknamePopup = false;
        this.nicknameInput = "";
        this.nicknameError = "";
        this.$alert && this.$alert("닉네임이 성공적으로 변경되었습니다.");
      } catch (error) {
        console.error("닉네임 저장 중 오류:", error);

        // 상세한 에러 메시지 제공
        if (error.code === "permission-denied") {
          this.nicknameError = "권한이 없습니다. 다시 로그인해주세요.";
        } else if (error.code === "unavailable") {
          this.nicknameError = "네트워크 연결을 확인해주세요.";
        } else {
          this.nicknameError = `닉네임 저장 중 오류가 발생했습니다: ${error.message}`;
        }
      } finally {
        this.nicknameLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.nickname-content {
  padding: 50px 24px 40px 24px;
  text-align: center;
}

.nickname-title {
  margin-bottom: 18px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.nickname-input {
  padding: 10px 16px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  width: 80%;
  margin-bottom: 8px;
  box-sizing: border-box;
}

.nickname-input:focus {
  outline: none;
  border-color: #8e7f7f;
  box-shadow: 0 0 0 2px rgba(142, 127, 127, 0.2);
}

.nickname-error {
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 8px;
  min-height: 20px;
}

.nickname-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.cancel-button {
  padding: 8px 24px;
  font-size: 16px;
  border-radius: 8px;
  background: #eee;
  color: #222;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover:not(:disabled) {
  background: #ddd;
}

.logout-button {
  padding: 8px 24px;
  font-size: 16px;
  border-radius: 8px;
  background: #f2f2f2;
  color: #e74c3c;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.logout-button:hover:not(:disabled) {
  background: #e8e8e8;
}

.save-button {
  padding: 8px 24px;
  font-size: 16px;
  border-radius: 8px;
  background: linear-gradient(45deg, #66bdff, #87ceeb);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 189, 255, 0.3);
}

.save-button:hover:not(:disabled) {
  background: linear-gradient(45deg, #4a9eff, #66bdff);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 189, 255, 0.4);
}

.cancel-button:disabled,
.logout-button:disabled,
.save-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 반응형 디자인 */
@media screen and (max-width: 480px) {
  .nickname-content {
    padding: 40px 20px 30px 20px;
  }

  .nickname-title {
    font-size: 18px;
    margin-bottom: 15px;
  }

  .nickname-input {
    width: 90%;
    font-size: 15px;
    padding: 12px 14px;
  }

  .nickname-actions {
    flex-direction: row;
    gap: 6px;
  }

  .cancel-button,
  .logout-button,
  .save-button {
    padding: 12px 24px;
    font-size: 15px;
  }
}
</style>
